import React, { useState, useEffect } from 'react'
import { Modal, Button, Icon, Input, Select, Tooltip, message } from 'antd'
import { useWardrobe } from '../../../../hooks/useWardrobe'
import { OutfitGrid, useOutfitLibrary } from './shared'
import CreateOutfitModal from './CreateOutfitModal'
import WardrobeLibraryPickerModal from './WardrobeLibraryPickerModal'
import './WardrobeManagerModal.scss'

const { Option } = Select

/**
 * Modal for managing the global wardrobe library
 */
const WardrobeManagerModal = ({
  visible,
  onClose,
  formData,
  setFormData,
  videoId
}) => {
  const {
    deleteOutfit,
    createOutfit,
    isCreatingOutfit: isCreating,
    creationProgress: progress,
    stopPolling
  } = useWardrobe({ videoId })

  const {
    outfits: libraryOutfits,
    isLoading: isLibraryLoading,
    fetchIfNeeded,
    refresh,
    pagination
  } = useOutfitLibrary({ formData, setFormData, videoId })

  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({
    style: '',
    season: '',
    occasion: ''
  })
  const [sortBy, setSortBy] = useState('tag')
  const [sortOrder, setSortOrder] = useState('asc')
  const [pageSize, setPageSize] = useState(20)
  const [showMoreOptions, setShowMoreOptions] = useState(false)
  const [activeMode, setActiveMode] = useState('list')
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [selectedOutfit, setSelectedOutfit] = useState(null)

  useEffect(() => {
    if (visible) {
      fetchIfNeeded()
    }
    return () => stopPolling()
  }, [visible])

  const hasAnyFilter = !!filters.style || !!filters.season || !!filters.occasion
  const activeOptionCount =
    Object.values(filters).filter(Boolean).length +
    (sortBy !== 'tag' ? 1 : 0) +
    (sortOrder !== 'asc' ? 1 : 0) +
    (pageSize !== 20 ? 1 : 0)

  const fetchWithCurrentMode = async ({
    mode = activeMode,
    page = 1,
    nextFilters = filters,
    nextQuery = searchQuery,
    nextSortBy = sortBy,
    nextSortOrder = sortOrder,
    nextPageSize = pageSize
  } = {}) => {
    const trimmedQuery = (nextQuery || '').trim()

    if (mode === 'search' && trimmedQuery) {
      setActiveMode('search')
      return refresh({
        action: 'search',
        q: trimmedQuery,
        page,
        page_size: nextPageSize
      })
    }

    const shouldUseFilter =
      mode === 'filter' &&
      (!!nextFilters.style || !!nextFilters.season || !!nextFilters.occasion)

    if (shouldUseFilter) {
      setActiveMode('filter')
      return refresh({
        action: 'filter',
        style: nextFilters.style || undefined,
        season: nextFilters.season || undefined,
        occasion: nextFilters.occasion || undefined,
        page,
        page_size: nextPageSize,
        sort_by: nextSortBy,
        sort_order: nextSortOrder
      })
    }

    setActiveMode('list')
    return refresh({
      action: 'list',
      page,
      page_size: nextPageSize,
      sort_by: nextSortBy,
      sort_order: nextSortOrder
    })
  }

  const handleSearch = () => {
    const trimmed = searchQuery.trim()
    if (trimmed) {
      fetchWithCurrentMode({ mode: 'search', page: 1, nextQuery: trimmed })
      return
    }
    fetchWithCurrentMode({ mode: hasAnyFilter ? 'filter' : 'list', page: 1 })
  }

  const handleFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value || '' }
    setFilters(newFilters)
    const shouldFilter =
      !!newFilters.style || !!newFilters.season || !!newFilters.occasion
    fetchWithCurrentMode({
      mode: shouldFilter ? 'filter' : 'list',
      page: 1,
      nextFilters: newFilters
    })
  }

  const handleSortBy = value => {
    const nextSortBy = value || 'tag'
    setSortBy(nextSortBy)
    fetchWithCurrentMode({
      mode: activeMode,
      page: 1,
      nextSortBy
    })
  }

  const handleSortOrder = value => {
    const nextSortOrder = value || 'asc'
    setSortOrder(nextSortOrder)
    fetchWithCurrentMode({
      mode: activeMode,
      page: 1,
      nextSortOrder
    })
  }

  const handlePageSizeChange = value => {
    const nextPageSize = Number(value) || 20
    setPageSize(nextPageSize)
    fetchWithCurrentMode({
      mode: activeMode,
      page: 1,
      nextPageSize
    })
  }

  const handlePageChange = page => {
    fetchWithCurrentMode({
      mode: activeMode,
      page
    })
  }

  const handleDelete = async outfit => {
    try {
      await deleteOutfit(outfit.tag || outfit.id)
      message.success('Outfit deleted')
      refresh()
    } catch (err) {
      message.error('Failed to delete outfit')
    }
  }

  const handleOutfitClick = outfit => {
    setSelectedOutfit(outfit)
  }

  return (
    <>
      <Modal
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={920}
        centered
        className="wardrobe-manager-modal"
        title={null}
        closable={false}
        destroyOnClose
      >
        <div className="manager-container">
          {/* Header */}
          <div className="manager-header">
            <div className="manager-heading">
              <h2 className="manager-title">Manage Wardrobe</h2>
              <p>Browse and manage your global collection of outfits.</p>
            </div>
            <Button
              type="link"
              className="close-button"
              aria-label="Close wardrobe manager"
              onClick={onClose}
            >
              <Icon type="close" />
            </Button>
          </div>

          {/* Toolbar */}
          <div className="manager-toolbar">
            <div className="toolbar-primary-row">
              <div className="search-section">
                <Input.Group compact className="search-control">
                  <Input
                    aria-label="Search wardrobe outfits"
                    placeholder="Search outfits by name, tag, or description..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onPressEnter={handleSearch}
                  />
                  <Button type="primary" onClick={handleSearch}>
                    <Icon type="search" /> Search
                  </Button>
                </Input.Group>
              </div>

              <Button
                className={`more-options-button${
                  showMoreOptions ? ' more-options-button--open' : ''
                }`}
                aria-expanded={showMoreOptions}
                aria-controls="wardrobe-more-options"
                onClick={() => setShowMoreOptions(current => !current)}
              >
                <Icon type="filter" />
                <span>More options</span>
                {activeOptionCount > 0 && (
                  <span className="active-options-count">
                    {activeOptionCount}
                  </span>
                )}
                <Icon type={showMoreOptions ? 'up' : 'down'} />
              </Button>

              <div className="toolbar-actions">
                <Tooltip title="Refresh wardrobe">
                  <Button
                    className="refresh-button"
                    icon="reload"
                    aria-label="Refresh wardrobe"
                    onClick={() =>
                      fetchWithCurrentMode({ mode: activeMode, page: 1 })
                    }
                    loading={isLibraryLoading}
                  />
                </Tooltip>
                <Button
                  type="primary"
                  icon="plus"
                  className="add-outfit-button"
                  onClick={() => setIsCreateModalVisible(true)}
                >
                  Add Outfit
                </Button>
              </div>
            </div>

            {showMoreOptions && (
              <div id="wardrobe-more-options" className="filter-section">
                <div className="advanced-options-group">
                  <div className="advanced-options-title">Filter outfits</div>
                  <div className="advanced-options-fields advanced-options-fields--filters">
                    <div className="filter-field">
                      <label htmlFor="wardrobe-style">Style</label>
                      <Select
                        id="wardrobe-style"
                        placeholder="All styles"
                        value={filters.style || undefined}
                        onChange={val => handleFilter('style', val)}
                        allowClear
                        className="filter-select"
                      >
                        <Option value="formal">Formal</Option>
                        <Option value="casual">Casual</Option>
                        <Option value="sporty">Sporty</Option>
                        <Option value="business">Business</Option>
                      </Select>
                    </div>

                    <div className="filter-field">
                      <label htmlFor="wardrobe-season">Season</label>
                      <Select
                        id="wardrobe-season"
                        placeholder="All seasons"
                        value={filters.season || undefined}
                        onChange={val => handleFilter('season', val)}
                        allowClear
                        className="filter-select"
                      >
                        <Option value="summer">Summer</Option>
                        <Option value="winter">Winter</Option>
                        <Option value="spring">Spring</Option>
                        <Option value="fall">Fall</Option>
                        <Option value="all-season">All Season</Option>
                      </Select>
                    </div>

                    <div className="filter-field">
                      <label htmlFor="wardrobe-occasion">Occasion</label>
                      <Select
                        id="wardrobe-occasion"
                        placeholder="All occasions"
                        value={filters.occasion || undefined}
                        onChange={val => handleFilter('occasion', val)}
                        allowClear
                        className="filter-select"
                      >
                        <Option value="work">Work</Option>
                        <Option value="party">Party</Option>
                        <Option value="casual">Casual</Option>
                        <Option value="date">Date</Option>
                        <Option value="beach">Beach</Option>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="advanced-options-group advanced-options-group--display">
                  <div className="advanced-options-title">Display</div>
                  <div className="advanced-options-fields advanced-options-fields--display">
                    <div className="filter-field">
                      <label htmlFor="wardrobe-sort-by">Sort by</label>
                      <Select
                        id="wardrobe-sort-by"
                        value={sortBy}
                        onChange={handleSortBy}
                        className="filter-select"
                      >
                        <Option value="tag">Tag</Option>
                        <Option value="name">Name</Option>
                        <Option value="style">Style</Option>
                        <Option value="season">Season</Option>
                        <Option value="occasion">Occasion</Option>
                        <Option value="created_at">Created</Option>
                        <Option value="view_count">Views</Option>
                      </Select>
                    </div>

                    <div className="filter-field">
                      <label htmlFor="wardrobe-sort-order">Direction</label>
                      <Select
                        id="wardrobe-sort-order"
                        value={sortOrder}
                        onChange={handleSortOrder}
                        className="filter-select"
                      >
                        <Option value="asc">Ascending</Option>
                        <Option value="desc">Descending</Option>
                      </Select>
                    </div>

                    <div className="filter-field filter-field--page-size">
                      <label htmlFor="wardrobe-page-size">Per page</label>
                      <Select
                        id="wardrobe-page-size"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        className="filter-select"
                      >
                        <Option value={10}>10 items</Option>
                        <Option value={20}>20 items</Option>
                        <Option value={50}>50 items</Option>
                        <Option value={100}>100 items</Option>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Grid */}
          <div className="manager-content">
            <OutfitGrid
              outfits={libraryOutfits}
              onItemClick={handleOutfitClick}
              isLoading={isLibraryLoading}
              emptyMessage="No outfits found in your library. Add one to get started!"
              showDelete={true}
              onDelete={handleDelete}
              pagination={pagination}
              onPageChange={handlePageChange}
              clampDescriptions
            />
          </div>
        </div>

        <CreateOutfitModal
          visible={isCreateModalVisible}
          onClose={() => setIsCreateModalVisible(false)}
          onSuccess={() => refresh()}
          createOutfit={createOutfit}
          isCreating={isCreating}
          creationProgress={progress}
        />
      </Modal>

      <WardrobeLibraryPickerModal
        visible={!!selectedOutfit}
        onClose={() => setSelectedOutfit(null)}
        initialOutfit={selectedOutfit}
        mode="view"
        formData={formData}
        setFormData={setFormData}
        videoId={videoId}
      />
    </>
  )
}

export default WardrobeManagerModal
